import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { CircularFramework, LinearFramework } from "@/components/FrameworkDiagram";
import { ArrowRight } from "lucide-react";
import useSeo from "@/lib/useSeo";
import VriMark from "@/components/VriMark";

export default function Home() {
  useSeo({
    title: "The Value Realisation Framework — Langwood360",
    description: "Leadership is measured by the choices it creates. A manifesto and 20-minute board diagnostic by Steve Bird for organisations that intend to convert effort into durable enterprise value.",
    path: "/",
  });
  return (
    <div className="min-h-screen bg-linen text-oxford">
      <Navbar />

      {/* HERO — manifesto */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-16 lg:pt-24 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <div data-testid="hero-logo" className="mb-10 inline-block">
              <VriMark
                size={112}
                withWordmark
                palette="default"
                title="The Value Realisation Index"
              />
            </div>
            <div className="tick-label mb-8" data-testid="hero-eyebrow">A Manifesto · No. 001</div>
            <h1 className="display-serif text-5xl md:text-7xl lg:text-8xl">
              Leadership is measured by the <em className="italic">choices</em> it creates.
            </h1>
            <div className="rule my-10 max-w-md" />
            <p className="max-w-2xl text-lg md:text-xl leading-relaxed text-charcoal">
              And the quality of an organisation's outcomes is determined by the
              quality of its management alignment.
            </p>
            <p className="max-w-2xl text-lg md:text-xl leading-relaxed text-charcoal mt-6">
              Most organisations do not fail from a lack of effort. They fail
              from a slow, invisible leakage of value — between the strategy the
              Board defined and the reality the organisation is living.
            </p>
            <div className="mt-12 flex flex-wrap gap-4">
              <Link to="/diagnostic" data-testid="hero-cta-take-index" className="btn-oxford">
                Take the Index <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
              </Link>
              <Link to="/framework" data-testid="hero-cta-framework" className="btn-ghost">Read the Framework</Link>
            </div>
          </div>
          <div className="lg:col-span-4 hidden lg:flex items-end">
            <div className="w-full">
              <img
                src="https://customer-assets-v7afamib.emergentagent.net/job_vri-index/artifacts/ugswt8j1_Maze%20-%20Sand%20coloured.jpeg"
                alt="A leader considering the maze — the challenge of navigating without a framework"
                className="w-full h-[520px] object-cover"
              />
              <div className="tick-label mt-3">Fig. 001 — leadership without a framework</div>
            </div>
          </div>
        </div>
      </section>

      {/* Framework — linear + circular */}
      <section id="framework" className="border-t border-oxford/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <div className="tick-label mb-6">The Framework</div>
            <h2 className="display-serif text-4xl md:text-6xl">
              Five stages. <em className="italic">One</em> continuous cycle.
            </h2>
            <p className="mt-8 text-charcoal leading-relaxed max-w-md">
              Clarity precedes alignment. Alignment precedes execution.
              Execution creates value. Value creates choice. And every new
              choice returns the organisation to a higher clarity.
            </p>
            <p className="mt-4 text-charcoal leading-relaxed max-w-md italic">
              Great organisations do not complete this process. They live inside it.
            </p>
          </div>
          <div className="lg:col-span-3">
            <LinearFramework />
          </div>
          <div className="lg:col-span-4">
            <CircularFramework />
          </div>
        </div>
      </section>

      {/* Why this matters */}
      <section className="border-t border-oxford/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="tick-label mb-6">Why this matters</div>
            <h2 className="display-serif text-4xl md:text-5xl">
              Organisations lose value long before it appears in the numbers.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:col-start-6 space-y-8 text-lg leading-relaxed text-charcoal">
            <p>
              Financial performance is the <em>last</em> place value leakage
              becomes visible. By the time it shows up in the accounts, the
              root cause is already twelve to eighteen months old.
            </p>
            <p>
              What the Board actually needs is a leading indicator — a
              disciplined way of measuring, at any given moment, whether
              effort is being converted into durable enterprise value.
            </p>
            <p>
              That is what the Value Realisation Framework was
              built to provide. And it is what the Index lets a
              board measure in twenty minutes.
            </p>
          </div>
        </div>
      </section>

      {/* Assets */}
      <section className="border-t border-oxford/10 bg-parchment">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
          <div className="tick-label mb-6">The Assets</div>
          <h2 className="display-serif text-4xl md:text-5xl max-w-3xl">
            Six assets. One idea, applied.
          </h2>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-oxford/10">
            {[
              ["01", "The Manifesto", "The philosophy in a single reading.", "Published"],
              ["02", "The Framework", "The definitive visual model.", "Published"],
              ["03", "The Index", "The 20-minute board diagnostic.", "Available"],
              ["04", "The Review", "A structured half-day workshop.", "By invitation"],
              ["05", "The Annual Index", "Benchmarking across organisations.", "2027 edition"],
              ["06", "The Book", "The long-form argument.", "In development"],
            ].map(([n, t, d, s]) => (
              <div key={n} className="bg-linen p-10 min-h-[220px] flex flex-col justify-between">
                <div>
                  <div className="tick-label">Asset {n}</div>
                  <div className="display-serif text-3xl mt-6">{t}</div>
                  <p className="text-sm text-charcoal mt-3">{d}</p>
                </div>
                <div className="tick-label mt-8 text-moss">{s}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Meet Steve — LAST */}
      <section className="border-t border-oxford/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-4">
            <img
              src="https://customer-assets.emergentagent.com/job_vri-index/artifacts/ulxhrnaj_SBPicture1.jpg"
              alt="Steve Bird"
              className="w-full h-[520px] object-cover object-top"
            />
            <div className="tick-label mt-3">Steve Bird · Founder</div>
          </div>
          <div className="lg:col-span-7 lg:col-start-6">
            <div className="tick-label mb-6">Then, and only then —</div>
            <h2 className="display-serif text-4xl md:text-6xl">Meet Steve Bird.</h2>
            <p className="mt-8 text-lg leading-relaxed text-charcoal max-w-xl">
              Steve is the person who developed the Value Realisation
              Framework. He is the authority behind the Framework, because he
              has consistently applied it.
            </p>
            <p className="mt-6 text-lg leading-relaxed text-charcoal max-w-xl">
              For thirty years, in boardrooms across the UK, he has watched
              organisations lose value they did not know they had — and helped
              others recover it. This Framework is the distillation of his experience.
            </p>
            <Link to="/steve" data-testid="home-cta-meet-steve" className="btn-ghost mt-10">
              Read the story <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-oxford/10 bg-oxford text-linen">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="tick-label mb-6 text-linen/70">A twenty-minute diagnostic</div>
            <h2 className="display-serif text-4xl md:text-6xl">
              Where is value leaking from your organisation?
            </h2>
            <p className="mt-6 text-lg text-linen/80 max-w-xl">
              The Value Realisation Index gives every board a
              number — and a shared language.
            </p>
          </div>
          <div className="lg:col-span-4 lg:col-start-9 flex items-end">
            <Link to="/diagnostic" data-testid="home-footer-cta" className="btn-oxford bg-linen text-oxford border-linen hover:bg-parchment hover:text-oxford">
              Begin the Index <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
